import React, { useState, useCallback } from "react";
import { Helmet } from "react-helmet";

import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Hidden,
  Typography,
} from "@mui/material";

import { containerStyles, headerStyles } from "pages/settings/utils/styles";
import { NotificationSection } from "./components";
import { useOperator } from "globals/hooks";
import { moovsBlue, moovsBlueSelected } from "design-system/colors";
import { FeedbackModal } from "design-system/components/layout/SideNav/components/HelpFAB/components";
import { useNotificationSettings } from "./hooks/useNotificationSettings";

function NotificationsPage() {
  // hooks
  const {
    settings,
    driverReminderPendingJobsTextEnabled,
    driverReminderTripReminderTextsEnabled,
    driverStatusUpdateTextsEnabled,
    automatedEmailsEnabled,
    tripCancellationEmailEnabled,
  } = useOperator() || {};

  // state
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [smsExpanded, setSmsExpanded] = useState(true);
  const [emailExpanded, setEmailExpanded] = useState(true);

  // Get processed notification data from custom hook
  const {
    smsNotifications,
    emailNotifications,
    settingIsDisabledMap,
    loading,
  } = useNotificationSettings({
    settings,
    driverReminderPendingJobsTextEnabled,
    driverReminderTripReminderTextsEnabled,
    driverStatusUpdateTextsEnabled,
    automatedEmailsEnabled,
    tripCancellationEmailEnabled,
  });

  // Memoized expand handlers
  const handleSmsExpandChange = useCallback((expanded: boolean) => {
    setSmsExpanded(expanded);
  }, []);

  const handleEmailExpandChange = useCallback((expanded: boolean) => {
    setEmailExpanded(expanded);
  }, []);

  if (loading) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
        width="100%"
        height="100%"
      >
        <CircularProgress size={40} thickness={2} />
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>Notifications | Moovs</title>
      </Helmet>
      <Box sx={containerStyles}>
        <Box sx={headerStyles}>
          <Box display="flex" mt={2}>
            <Typography variant="h2">Notifications</Typography>
            <Chip
              label="BETA"
              size="small"
              sx={{
                py: 2,
                px: 1,
                fontSize: 14,
                ml: 2,
                bgcolor: moovsBlueSelected,
                color: moovsBlue,
                fontWeight: 600,
              }}
            />
          </Box>
          <Box mt={2} mb={1}>
            <Typography variant="body2">
              This feature is in beta and available to a limited group of
              operators. For now, existing settings still apply. Eventually, all
              notification controls will be moved here. Share feedback{" "}
              <Typography
                variant="body2"
                sx={{
                  display: "inline",
                  cursor: "pointer",
                  color: moovsBlue,
                  textDecoration: "underline",
                }}
                onClick={() => setFeedbackModalOpen(true)}
              >
                here
              </Typography>
              .
            </Typography>
          </Box>
        </Box>
        <Hidden mdDown>
          <Box px={3}>
            <Divider />
          </Box>
        </Hidden>
        <Box my={3}>
          <NotificationSection
            title="SMS Notifications"
            notifications={smsNotifications}
            expanded={smsExpanded}
            onExpandChange={handleSmsExpandChange}
            settingIsDisabledMap={settingIsDisabledMap}
          />

          <NotificationSection
            title="Email Notifications"
            notifications={emailNotifications}
            expanded={emailExpanded}
            onExpandChange={handleEmailExpandChange}
            settingIsDisabledMap={settingIsDisabledMap}
          />
        </Box>
      </Box>
      <FeedbackModal
        open={feedbackModalOpen}
        onClose={() => setFeedbackModalOpen(false)}
      />
    </>
  );
}

export default NotificationsPage;
